import React, { useState } from 'react';
import {
  Alert, Box, Button, Card, CardContent, Chip, FormControlLabel, Grid, MenuItem, Stack, Switch, TextField, Typography,
} from '@mui/material';
import AddTaskIcon from '@mui/icons-material/AddTask';
import ChecklistIcon from '@mui/icons-material/Checklist';
import BotAvatar from '../components/BotAvatar';
import PageHeader from '../components/PageHeader';
import StateBoundary from '../components/StateBoundary';
import TaskDialog from '../components/TaskDialog';
import { useSession } from '../viewmodels/SessionViewModel';
import { COLUMNS, useTasksViewModel } from '../viewmodels/TasksViewModel';

const PRIORITY_COLOR = { high: 'error', normal: 'default', low: 'default' };

function formatDue(date) {
  if (!date) return '';
  const d = new Date(`${date}T00:00:00`);
  return d.toLocaleDateString(undefined, { day: 'numeric', month: 'short' });
}

function TaskCard({ task, isOwner, busy, onMove, onEdit }) {
  const next = {
    todo: [['in_progress', 'Start'], ['done', 'Done']],
    in_progress: [['done', 'Done'], ['blocked', 'Blocked']],
    blocked: [['in_progress', 'Unblock'], ['done', 'Done']],
    done: [['todo', 'Reopen']],
  }[task.status] || [];
  return (
    <Card sx={{ mb: 1.5, borderLeft: 4, borderLeftColor: task.overdue ? 'error.main' : 'transparent' }}>
      <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
        <Typography fontWeight={600} sx={{ textDecoration: task.status === 'done' ? 'line-through' : 'none' }}>{task.title}</Typography>
        {task.description && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
            {task.description}
          </Typography>
        )}
        <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mt: 1 }}>
          {task.assignee ? (
            <Chip size="small" variant="outlined" avatar={<Box sx={{ display: 'flex' }}><BotAvatar bot={task.assignee} size={20} /></Box>} label={task.assignee.name} />
          ) : <Chip size="small" variant="outlined" label="Nobody yet" />}
          {task.priority === 'high' && <Chip size="small" color={PRIORITY_COLOR.high} label="High" />}
          {task.due_date && <Chip size="small" color={task.overdue ? 'error' : 'default'} variant={task.overdue ? 'filled' : 'outlined'} label={`${task.overdue ? 'Overdue ' : 'Due '}${formatDue(task.due_date)}`} />}
        </Stack>
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.75 }}>{`Added by ${task.created_by.name}`}</Typography>
        {isOwner && (
          <Stack direction="row" spacing={0.5} sx={{ mt: 0.75 }} flexWrap="wrap" useFlexGap>
            {next.map(([status, label]) => (
              <Button key={status} size="small" disabled={busy} onClick={() => onMove(task, status)}>{label}</Button>
            ))}
            <Button size="small" color="inherit" disabled={busy} onClick={() => onEdit(task)}>Edit</Button>
          </Stack>
        )}
      </CardContent>
    </Card>
  );
}

export default function TasksView() {
  const { isOwner } = useSession();
  const vm = useTasksViewModel();
  const [dialog, setDialog] = useState({ open: false, task: null });

  const save = async (fields) => {
    const ok = dialog.task ? await vm.updateTask(dialog.task.id, fields) : await vm.createTask(fields);
    if (ok) setDialog({ open: false, task: null });
  };
  const columns = COLUMNS.filter((c) => vm.showDone || c.status !== 'done');

  return (
    <>
      <PageHeader
        title="Tasks"
        subtitle="One shared to-do list for you and your team. Team members add follow-ups here too."
        actions={isOwner && (
          <Button variant="contained" startIcon={<AddTaskIcon />} onClick={() => { vm.clearSaveError(); setDialog({ open: true, task: null }); }}>New task</Button>
        )}
      />
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }} sx={{ mb: 2.5 }}>
        <TextField select size="small" label="Team member" value={vm.assignee} onChange={(e) => vm.setAssignee(e.target.value)} sx={{ minWidth: 220 }}>
          <MenuItem value="">Everyone</MenuItem>
          {vm.bots.map((b) => <MenuItem key={b.id} value={b.id}>{b.name}</MenuItem>)}
        </TextField>
        <FormControlLabel control={<Switch checked={vm.showDone} onChange={(e) => vm.setShowDone(e.target.checked)} />} label="Show completed" />
      </Stack>
      {vm.saveError && !dialog.open && <Alert severity="error" sx={{ mb: 2 }}>{vm.saveError}</Alert>}
      <StateBoundary
        loading={vm.loading}
        error={vm.error}
        onRetry={vm.reload}
        isEmpty={vm.tasks.length === 0}
        empty={{
          icon: <ChecklistIcon fontSize="large" />,
          title: vm.assignee ? 'No tasks for this team member' : 'Nothing to do right now',
          body: 'Add a task yourself, or ask a team member in the chat to keep track of a follow-up.',
        }}
      >
        <Grid container spacing={2}>
          {columns.map((col) => {
            const items = vm.tasks.filter((t) => t.status === col.status);
            return (
              <Grid key={col.status} size={{ xs: 12, sm: 6, lg: 12 / columns.length }}>
                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                  {`${col.label} (${items.length})`}
                </Typography>
                {items.length === 0 && <Typography variant="body2" color="text.disabled" sx={{ py: 1 }}>Nothing here</Typography>}
                {items.map((t) => (
                  <TaskCard
                    key={t.id}
                    task={t}
                    isOwner={isOwner}
                    busy={vm.saving}
                    onMove={(task, status) => vm.updateTask(task.id, { status })}
                    onEdit={(task) => { vm.clearSaveError(); setDialog({ open: true, task }); }}
                  />
                ))}
              </Grid>
            );
          })}
        </Grid>
      </StateBoundary>
      <TaskDialog
        open={dialog.open}
        task={dialog.task}
        bots={vm.bots}
        saving={vm.saving}
        error={vm.saveError}
        onClose={() => setDialog({ open: false, task: null })}
        onSave={save}
      />
    </>
  );
}
